import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaUser, FaClipboardCheck, FaChartBar, FaCog, FaUsers } from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig.js';
import CollapsibleNavbar from "./CollapsibleNavbar.jsx";
import "../styles/theme.css";
import CreateUserForm from "./CreateUserFormModern.jsx";
import ConfigurationForm from "./ConfigurationFormModern.jsx";
import ConstraintsForm from "./ConstraintsFormModern.jsx";
import ManageUsers from './ManageUsers.jsx';
import ModifyUser from "./ModifyUser.jsx";
import ImportUsers from './ImportUsers.jsx';
import ExportUsers from './ExportUsers.jsx';
import AdminResourceCenter from "./AdminResourceCenter.jsx";
import AdminEvaluationCenter from "./AdminEvaluationCenter.jsx";
import DueEvents from './DueEvents.jsx';
import ManageCurriculums from "./ManageCurriculumsWorkspace.jsx";
import FormBuilderWrapper from "./FormBuilderWrapper.jsx";
import AdminPreceptorAvailabilityManager from "./AdminPreceptorAvailabilityManager.jsx";
import MakeAnnouncement from "./MakeAnnouncement.jsx";
import ChangePassword from "./ChangePassword.jsx";
import ScheduleByStudentPage from "./schedules/ScheduleByStudentPage";
import ScheduleByPreceptorPage from "./schedules/ScheduleByPreceptorPage";
import ScheduleBySpecialityPage from "./schedules/ScheduleBySpecialityPage";
import ScheduleByInstitutionPage from "./schedules/ScheduleByInstitutionPage";
import ScheduleByRotationPage from "./schedules/ScheduleByRotationPage";
import SpecialityInstitutionManager from "./SpecialityInstitutionManager.jsx";
import ManageRanks from "./ManageRanks.jsx";
import AllEvaluationsPage from "./AllEvaluationsPage.jsx";
import StudentAttendancePage from "./StudentAttendancePage.jsx";
import LoadingSpinner from './LoadingSpinner';
import FailurePage from './FailurePage.jsx';
import MyOrganizationPage from "./MyOrganizationPage.jsx";
import { normalizeUser } from '../utils/userUtils';
import { isImpersonating, restoreAdminSession } from '../utils/auth';

// Views that already render their own full-width page shell (the `mc-*` /
// universal-white-container layout used by Manage Curricula, or a bespoke one).
// Wrapping them in a `content-box` would nest a white card inside a white card.
const SELF_SHELLED_VIEWS = new Set([
    "change-password",
    "my-account",
    "config",
    "manage-ranks",
    "manage-curricula",
    "manage-curriculums",
    "create-user",
    "admin-resource-center",
    "manage-users",
    "import-students",
    "import-preceptors",
    "import-admins",
    "import-users",
    "form-builder",
    "speciality-institution-manager",
    "make-announcement",
    "preceptor-availability",
    "my-organization",
    "all-evaluations",
    "student-attendance",
]);

const StandaloneAdminDashboard = () => {
    const [adminData, setAdminData] = useState(null);
    const [error, setError] = useState("");
    const [showFormBuilder, setShowFormBuilder] = useState(false);
    const [loadKey, setLoadKey] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const { view } = useParams();

    // Initialize currentView from the URL. `view` comes from the /:view route
    // param; the pathname fallback keeps the page correct if the route is ever
    // matched by a splat pattern, where `view` would be undefined.
    const pathView = location.pathname.split('/').filter(Boolean)[1];
    const currentView = view || pathView || "dashboard";

    // Update URL when view changes
    const handleViewChange = (newView) => {
        if (newView === "dashboard") {
            navigate("/StandaloneAdminDashboard", { replace: true });
        } else {
            navigate(`/StandaloneAdminDashboard/${newView}`, { replace: true });
        }
    };

    useEffect(() => {
        const fetchAdminData = async () => {
            try {
                // Wait for token to be available
                let retries = 0;
                while (!localStorage.getItem('access_token') && retries < 10) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    retries++;
                }

                if (!localStorage.getItem('access_token')) {
                    console.error('No token found after waiting');
                    setError("Authentication failed. Please login again.");
                    navigate('/');
                    return;
                }

                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('🔑 Token found, making API calls...');

                const response = await axiosInstance.get("/auth/admin/dashboard");
                setAdminData(normalizeUser(response.data?.data || {}));
            } catch (err) {
                console.error('❌ Dashboard API error:', err);

                if (err.response?.status === 403 && isImpersonating()) {
                    const restored = restoreAdminSession();
                    if (restored.success) {
                        window.location.href = restored.returnPath || '/StandaloneAdminDashboard/manage-users';
                        return;
                    }
                }

                setError("Failed to fetch admin data. Please try again later.");
            }
        };

        fetchAdminData();
    }, [navigate, loadKey]);

    const renderDashboardContent = () => {
        return (
            <div className="modern-dashboard">
                <div className="dashboard-header">
                    <h1 className="dashboard-title">
                        Welcome, {adminData.firstname} {adminData.lastname}
                    </h1>
                    <p className="dashboard-subtitle">Standalone Administrator Dashboard</p>
                </div>

                <div className="dashboard-grid">
                    <div className="dashboard-card dashboard-card--dark">
                        <div className="card-header">
                            <FaUser style={{ color: '#3b82f6' }} />
                            <h2 className="card-title">Personal Information</h2>
                        </div>
                        <div className="card-content">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Admin ID</span>
                                    <span className="info-value">{adminData._id}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Email</span>
                                    <span className="info-value">{adminData.email}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Role</span>
                                    <div className="info-value info-value-badges" style={{ justifyContent: 'center', width: '100%' }}>
                                        <span className="role-badge" style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.2)' }}>
                                            Standalone Administrator
                                        </span>
                                    </div>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Phone Number</span>
                                    <span className="info-value">{adminData.phone}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-card dashboard-card--dark">
                        <div className="card-header">
                            <FaChartBar style={{ color: '#10b981' }} />
                            <h2 className="card-title">System Overview</h2>
                        </div>
                        <div className="card-content">
                            <div style={{ textAlign: 'center', padding: '1rem' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🛡️</div>
                                <h4 style={{ color: '#ffffff', margin: '0 0 0.5rem 0' }}>Security & Integrity</h4>
                                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', margin: 0 }}>System is currently under global administrative protection.</p>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Due Events Section */}
                <DueEvents 
                    userId={adminData._id} 
                    userRole="admin" 
                />
            </div>
        );
    };

    const renderContent = () => {
        switch (currentView) {
            case "schedule-student":
                return <ScheduleByStudentPage />;
            case "schedule-preceptor":
                return <ScheduleByPreceptorPage />;
            case "schedule-speciality":
                return <ScheduleBySpecialityPage />;
            case "schedule-institution":
                return <ScheduleByInstitutionPage />;
            case "schedule-rotation":
                return <ScheduleByRotationPage />;
            case "create-user":
                return <CreateUserForm />;
            case "config":
                return <ConfigurationForm />;
            case "constraints":
                return <ConstraintsForm />;
            case "manage-users":
                return <ManageUsers />;
            case "my-account":
                return <ModifyUser userId={adminData?._id} userType="standalone-admin" isAdmin={true} />;
            case "change-password":
                return <ChangePassword />;
            case "import-students":
                return <ImportUsers initialMode="students" />;
            case "import-preceptors":
                return <ImportUsers initialMode="preceptors" />;
            case "import-admins":
                return <ImportUsers initialMode="admins" />;
            case "import-users":
                return <ImportUsers />;
            case "export-students":
                return <ExportUsers userType="students" />;
            case "export-preceptors":
                return <ExportUsers userType="preceptors" />;
            case "export-admins":
                return <ExportUsers userType="admins" />;
            case "admin-resource-center":
                return <AdminResourceCenter />;
            case "speciality-institution-manager":
                return <SpecialityInstitutionManager />;
            case "manage-ranks":
                return <ManageRanks />;
            // The navbar dispatches "manage-curricula"; "manage-curriculums" is kept
            // as an alias so both spellings resolve.
            case "manage-curricula":
            case "manage-curriculums":
                return <ManageCurriculums />;
            case "evaluation-center":
                return <AdminEvaluationCenter userType="admin" userData={adminData} />;
            case "all-evaluations":
                return <AllEvaluationsPage />;
            case "student-attendance":
                return <StudentAttendancePage adminMode={true} />;
            case "my-organization":
                return <MyOrganizationPage canEdit={true} />;
            case "preceptor-availability":
                return <AdminPreceptorAvailabilityManager />;
            case "make-announcement":
                return <MakeAnnouncement />;
            case "form-builder":
                setShowFormBuilder(true);
                return null;
            case "dashboard":
            default:
                return renderDashboardContent();
        }
    };

    if (error) {
        return (
            <FailurePage
                title="Oops, standalone admin dashboard failed"
                message={error}
                actionLabel={error.includes('login') ? 'Go to login' : 'Retry'}
                onAction={() => {
                    if (error.includes('login')) {
                        navigate('/');
                        return;
                    }
                    setError('');
                    setAdminData(null);
                    setLoadKey((key) => key + 1);
                }}
            />
        );
    }

    if (!adminData) {
        return (
            <div className="page-container">
                <div className="content-box">
                    <LoadingSpinner text="Loading standalone admin dashboard..." variant="page" />
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <CollapsibleNavbar
                setCurrentView={handleViewChange}
                userType="standalone_admin"
                isStandaloneAdmin={true}
                isAdmin={true}
                onFormBuilderClick={() => setShowFormBuilder(true)}
            />
            {SELF_SHELLED_VIEWS.has(currentView) ? (
                renderContent()
            ) : (
                <div className="content-box">
                    <div className="dashboard-container">
                        {renderContent()}
                    </div>
                </div>
            )}
            
            {/* Form Builder Modal */}
            {showFormBuilder && (
                <FormBuilderWrapper onClose={() => setShowFormBuilder(false)} />
            )}
        </div>
    );
};

export default StandaloneAdminDashboard;
